import React, { useContext, useEffect, useState } from 'react';
import { withPrefix } from 'gatsby';
import { Container } from '@vfuk/core-container';
import { Heading } from '@vfuk/core-heading';
import { Paragraph } from '@vfuk/core-paragraph';
import LinkWithIcon from '@vfuk/core-link-with-icon';
import { SectionRedCampaignBannerProps } from './types/SectionRedCampaignBanner.types';
import {
  DesktopContainerWrapper,
  DesktopContentWrapper,
  DesktopImageWrapper,
  MobileContainerWrapper,
  MobileContentWrapper,
  MobileImageWrapper,
  BetaLabel,
} from './styles/SectionRedCampaignBanner.styles';
import { DirectionContext } from '../../../../contexts/DirectionContext';
import fireButtonAndLinkClicksEvent from '../../../../helpers/fireButtonAndLinkClicksEvent';
import { addHashIfMissing } from '../../../../helpers/navigationHelpers';
import CtaButtonRow from '../../Elements/CtaButtons/CtaButtonRow';

const MOBILE_VIEW_WIDTH = 640;

const SectionRedCampaignBanner = ({
  title,
  bodyText,
  linkText,
  targetUrl,
  linkType,
  scrollToId,
  backgroundColor,
  image,
  label,
  buttonRow,
}: SectionRedCampaignBannerProps) => {
  const isMobile = checkWindowSize();
  const direction = useContext(DirectionContext);
  const desktopImage = image.imageTitle.file.url || '';
  const desktopImageAlt =
    image.imageTitle.description || image.imageTitle.title;
  const mobileImage = image.mobileBackgroundImage?.file.url || '';
  const mobileImageAlt =
    image.mobileBackgroundImage?.description ||
    image.mobileBackgroundImage?.title ||
    '';

  function checkWindowSize() {
    const [isMobile, setIsMobile] = useState(false);
    if (typeof window !== 'undefined') {
      useEffect(() => {
        function handleResize() {
          if (window.innerWidth <= MOBILE_VIEW_WIDTH) {
            setIsMobile(true);
          }
        }
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
      }, []);
    }
    return isMobile;
  }

  const onClickBanner = () => {
    if (buttonRow) {
      return;
    }

    fireButtonAndLinkClicksEvent({
      event_label: linkText,
      event_action: 'link click',
    });
  };

  const getContainerHref = (): string | undefined => {
    if (buttonRow) {
      return;
    }

    return linkType === 'scroll'
      ? addHashIfMissing(scrollToId || '')
      : withPrefix(targetUrl);
  };

  const getLinkWithIconHref = (): string => {
    return linkType === 'scroll'
      ? addHashIfMissing(scrollToId || '')
      : withPrefix(targetUrl);
  };

  if (isMobile) {
    return (
      <MobileContainerWrapper backgroundColor={backgroundColor}>
        <Container
          href={getContainerHref()}
          appearance="alt2"
          onClick={buttonRow ? undefined : onClickBanner}
        >
          <MobileImageWrapper>
            <img src={mobileImage} alt={mobileImageAlt} />
          </MobileImageWrapper>
          <MobileContentWrapper isRtl={direction === 'rtl'}>
            <Heading size={2} text={title} inverse />
            <Paragraph inverse appearance="primary">
              {bodyText}
            </Paragraph>
            {linkText && (
              <LinkWithIcon
                appearance="secondary"
                inverse
                href={getLinkWithIconHref()}
                text={linkText}
                icon={{
                  name: 'chevron-right',
                  justify: 'right',
                }}
              />
            )}
            <CtaButtonRow {...buttonRow} spacing={5} />
          </MobileContentWrapper>
        </Container>
      </MobileContainerWrapper>
    );
  } else {
    return (
      <DesktopContainerWrapper backgroundColor={backgroundColor}>
        <Container
          href={getContainerHref()}
          appearance="alt2"
          onClick={buttonRow ? undefined : onClickBanner}
        >
          <DesktopContentWrapper isRtl={direction === 'rtl'}>
            {label && (
              <BetaLabel>
                <span>{label}</span>
              </BetaLabel>
            )}
            <Heading size={2} text={title} inverse />
            <Paragraph inverse appearance="primary">
              {bodyText}
            </Paragraph>
            {linkText && (
              <LinkWithIcon
                appearance="secondary"
                inverse
                href={getLinkWithIconHref()}
                text={linkText}
                icon={{
                  name: 'chevron-right',
                  justify: 'right',
                }}
              />
            )}
            <CtaButtonRow {...buttonRow} spacing={5} />
          </DesktopContentWrapper>
          <DesktopImageWrapper>
            <img src={desktopImage} alt={desktopImageAlt} />
          </DesktopImageWrapper>
        </Container>
      </DesktopContainerWrapper>
    );
  }
};

export default SectionRedCampaignBanner;
